import * as React from 'react';
import classNames from 'classnames';
import { Button } from '../../../atoms/button/button';
import { useAutoControlled } from '../../../hooks';

interface SideNavigationHeaderProps
    extends React.HTMLAttributes<HTMLDivElement> {
    label?: string;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const SideNavigationHeader = React.forwardRef<
    HTMLDivElement,
    SideNavigationHeaderProps
>(
    (
        {
            className,
            open,
            defaultOpen,
            onOpenChange,
            children,
            label,
            ...rest
        },
        ref,
    ) => {
        const [isOpen, setOpen] = useAutoControlled({
            defaultValue: defaultOpen,
            value: open,
            initialValue: false,
        });

        const classes = classNames('m-side-navigation__header', {
            [className]: className,
        });

        function handleOpenChange(o: boolean) {
            if (typeof onOpenChange === 'function') {
                onOpenChange(o);
            }
            setOpen(o);
        }

        // this is needed because FROK tries to position the trigger button based on CSS but we do not render it at all
        const cssStyleFix: React.CSSProperties = {
            position: 'static',
            left: 0,
        };

        return (
            <div className={classes} {...rest} style={cssStyleFix} ref={ref}>
                {isOpen ? (
                    <>
                        <div className="m-side-navigation__header__label -size-l highlight">
                            {children || label}
                        </div>
                        <Button
                            style={cssStyleFix}
                            type="button"
                            mode="integrated"
                            icon="close"
                            aria-label="Close Side Navigation"
                            aria-haspopup="false"
                            className="m-side-navigation__header__trigger"
                            tabIndex={0}
                            onClick={() => handleOpenChange(false)}
                        />
                    </>
                ) : (
                    <Button
                        style={cssStyleFix}
                        type="button"
                        mode="integrated"
                        icon="list-view-mobile"
                        aria-label="Open Side Navigation"
                        aria-haspopup="false"
                        className="m-side-navigation__header__trigger"
                        tabIndex={0}
                        onClick={() => handleOpenChange(true)}
                    />
                )}
            </div>
        );
    },
);

SideNavigationHeader.displayName = 'SideNavigationHeader';

export { SideNavigationHeader };
export type { SideNavigationHeaderProps };
